import { addDays, addMonthsClamped, getNextWeekday } from './utils.js';

export type RelativeDateUnit = 'days' | 'weeks' | 'months' | 'years';

export type RelativeDateOffset = {
  amount: number;
  unit: RelativeDateUnit;
};

type NextDateUnitTarget = {
  kind: 'unit';
  name: string;
  aliases: string[];
  unit: 'weeks' | 'months';
};

type NextDateWeekdayTarget = {
  kind: 'weekday';
  name: string;
  aliases: string[];
  weekday: number;
};

export type NextDateTarget = NextDateUnitTarget | NextDateWeekdayTarget;

type RelativeDateUnitDefinition = {
  unit: RelativeDateUnit;
  singular: string;
  aliases: string[];
};

export const RELATIVE_DATE_UNITS: RelativeDateUnitDefinition[] = [
  {
    unit: 'days',
    singular: 'day',
    aliases: ['day', 'days', 'd'],
  },
  {
    unit: 'weeks',
    singular: 'week',
    aliases: ['week', 'weeks', 'wk', 'wks', 'w'],
  },
  {
    unit: 'months',
    singular: 'month',
    aliases: ['month', 'months', 'mo', 'mos'],
  },
  {
    unit: 'years',
    singular: 'year',
    aliases: ['year', 'years', 'yr', 'yrs', 'y'],
  },
];

export const NEXT_DATE_TARGETS: NextDateTarget[] = [
  {
    kind: 'unit',
    name: 'Next week',
    aliases: ['week', 'wk'],
    unit: 'weeks',
  },
  {
    kind: 'unit',
    name: 'Next month',
    aliases: ['month'],
    unit: 'months',
  },
  {
    kind: 'weekday',
    name: 'Next Sunday',
    aliases: ['sunday', 'sun', 'su'],
    weekday: 0,
  },
  {
    kind: 'weekday',
    name: 'Next Monday',
    aliases: ['monday', 'mon', 'mo'],
    weekday: 1,
  },
  {
    kind: 'weekday',
    name: 'Next Tuesday',
    aliases: ['tuesday', 'tues', 'tue', 'tu'],
    weekday: 2,
  },
  {
    kind: 'weekday',
    name: 'Next Wednesday',
    aliases: ['wednesday', 'weds', 'wed', 'we'],
    weekday: 3,
  },
  {
    kind: 'weekday',
    name: 'Next Thursday',
    aliases: ['thursday', 'thurs', 'thur', 'thu', 'th'],
    weekday: 4,
  },
  {
    kind: 'weekday',
    name: 'Next Friday',
    aliases: ['friday', 'fri', 'fr'],
    weekday: 5,
  },
  {
    kind: 'weekday',
    name: 'Next Saturday',
    aliases: ['saturday', 'sat', 'sa'],
    weekday: 6,
  },
];

const ONE_WORDS = new Set(['a', 'an', 'one']);

function normalizeInput(input: string) {
  return input.trim().toLocaleLowerCase().replace(/\s+/g, ' ');
}

function parseAmount(input: string): number | undefined {
  if (ONE_WORDS.has(input)) return 1;
  if (!/^\d+$/.test(input)) return;

  const amount = Number(input);
  if (!Number.isSafeInteger(amount) || amount <= 0) return;
  return amount;
}

function unitByAlias(input: string) {
  return RELATIVE_DATE_UNITS.find(({ aliases }) => aliases.includes(input));
}

function parseAmountAndUnitFragment(input: string) {
  const normalized = normalizeInput(input);
  if (!normalized) return;

  const spaced = /^(a|an|one|\d+)(?:\s+([a-z]*))?$/.exec(normalized);
  const compact = /^(\d+)([a-z]+)$/.exec(normalized);
  const match = compact ?? spaced;
  if (!match) return;

  const amount = parseAmount(match[1]);
  if (!amount) return;

  return { amount, fragment: match[2] ?? '' };
}

/**
 * Parses one relative date duration such as `13 days`, `4wk`, `a month`, or
 * `one year`. Multiple duration parts are deliberately rejected.
 */
export function parseRelativeDateOffset(
  input: string
): RelativeDateOffset | null {
  const parsed = parseAmountAndUnitFragment(input);
  if (!parsed?.fragment) return null;

  const definition = unitByAlias(parsed.fragment);
  if (!definition) return null;

  return { amount: parsed.amount, unit: definition.unit };
}

/**
 * Generates date-unit candidates from a complete or partially typed duration.
 * `/in 13` yields four rows; `/in 13 da` yields only `13 days`.
 */
export function getRelativeDateOffsetCandidates(
  input: string
): RelativeDateOffset[] {
  const parsed = parseAmountAndUnitFragment(input);
  if (!parsed) return [];

  const exact = unitByAlias(parsed.fragment);
  if (exact) return [{ amount: parsed.amount, unit: exact.unit }];

  const definitions = parsed.fragment
    ? RELATIVE_DATE_UNITS.filter(({ aliases }) =>
        aliases.some(alias => alias.startsWith(parsed.fragment))
      )
    : RELATIVE_DATE_UNITS;

  return definitions.map(({ unit }) => ({ amount: parsed.amount, unit }));
}

export function formatRelativeDateOffset({ amount, unit }: RelativeDateOffset) {
  const definition = RELATIVE_DATE_UNITS.find(entry => entry.unit === unit);
  if (!definition) return `${amount} ${unit}`;
  return `${amount} ${amount === 1 ? definition.singular : unit}`;
}

export function resolveRelativeDateOffset(
  date: Date,
  { amount, unit }: RelativeDateOffset
) {
  if (!Number.isSafeInteger(amount) || amount <= 0) {
    return new Date(Number.NaN);
  }

  switch (unit) {
    case 'days':
      return addDays(date, amount);
    case 'weeks':
      return addDays(date, amount * 7);
    case 'months':
      return addMonthsClamped(date, amount);
    case 'years':
      return addMonthsClamped(date, amount * 12);
  }
}

/**
 * Returns all exact matches when the input is a known alias, otherwise all
 * prefix matches. `/next m` is ambiguous (Monday or month), while the repeat
 * weekday aliases `/next mo` and `/next mon` resolve only to Monday.
 */
export function getNextDateCandidates(input: string): NextDateTarget[] {
  const normalized = normalizeInput(input);
  if (!normalized) return NEXT_DATE_TARGETS;

  const exact = NEXT_DATE_TARGETS.filter(({ aliases }) =>
    aliases.includes(normalized)
  );
  if (exact.length > 0) return exact;

  return NEXT_DATE_TARGETS.filter(({ aliases }) =>
    aliases.some(alias => alias.startsWith(normalized))
  );
}

export function parseNextDateTarget(input: string): NextDateTarget | null {
  const normalized = normalizeInput(input);
  if (!normalized) return null;

  const exact = NEXT_DATE_TARGETS.filter(({ aliases }) =>
    aliases.includes(normalized)
  );
  return exact.length === 1 ? exact[0] : null;
}

export function resolveNextDate(date: Date, target: NextDateTarget) {
  return target.kind === 'weekday'
    ? getNextWeekday(date, target.weekday)
    : target.unit === 'weeks'
      ? addDays(date, 7)
      : addMonthsClamped(date, 1);
}

export function isValidDate(date: Date) {
  return !Number.isNaN(date.getTime());
}
